import React, { useMemo, useRef, useState } from "react";
import { companyManagementUrl, userManagementUrl } from "../config";
import Swal from "sweetalert2";
import * as yup from "yup";
import axios from "axios";

// ---- Defaults (user never has to type these) ----
const DEFAULT_REGION_NAME = "Region A";
const DEFAULT_BRANCH_NAME = "Branch A";
const DEFAULT_BRANCH_LOCATION = "HQ";
const DEFAULT_BRANCH_PHONE = "0712345678";
const DEFAULT_ADDRESS = "Not provided";
const TRIAL_DAYS = 14;

// ---- Resumable progress (survives close/reopen/refresh of the tab) ----
const PROGRESS_KEY = "trialWizardProgress";

const EMPTY_PROGRESS = {
  companyId: null,
  companyName: null, // name the company was created with
  logoDone: false,
  regionId: null,
  branchDone: false,
  userDone: false,
};

const loadProgress = () => {
  try {
    const raw = sessionStorage.getItem(PROGRESS_KEY);
    return raw ? { ...EMPTY_PROGRESS, ...JSON.parse(raw) } : { ...EMPTY_PROGRESS };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
};

const saveProgress = (p) => {
  try {
    sessionStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
  } catch {
    // storage unavailable: in-memory ref still protects this session
  }
};

const clearProgress = () => {
  try {
    sessionStorage.removeItem(PROGRESS_KEY);
  } catch {
    /* ignore */
  }
};

const logoValidationSchema = yup.object().shape({
  logo: yup
    .mixed()
    .test("fileSize", "The file is too large (max 2MB)", (value) =>
      value ? value.size <= 2 * 1024 * 1024 : true
    )
    .test("fileType", "Unsupported file format (JPEG/PNG only)", (value) =>
      value ? ["image/jpeg", "image/png"].includes(value.type) : true
    ),
});

// Upload company logo
const uploadDocument = async (file, metadata, uri) => {
  const formData = new FormData();

  formData.append("Logo", file);
  formData.append("metadata", JSON.stringify(metadata));

  return axios.post(uri, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// first_last, lowercase, no spaces/special chars
const buildUserName = (first, last) => {
  const clean = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

  const f = clean(first);
  const l = clean(last);

  return f && l ? `${f}_${l}` : f || l;
};

const parseResults = (res) =>
  typeof res.data?.results === "string"
    ? JSON.parse(res.data.results)
    : res.data?.results;

const inputClass =
  "w-full px-4 py-2 rounded bg-[#141c2b] border border-[#232624] text-white";

const TrialFormWizard = ({ onClose }) => {
  // Load saved progress once, so a retry (or reopening the wizard)
  // continues from the first step that has not succeeded yet.
  const progress = useRef(null);
  if (progress.current === null) progress.current = loadProgress();

  const [formData, setFormData] = useState({
    companyName: progress.current.companyName || "",
    logo: null,
    firstName: "",
    lastName: "",
    idno: "",
    email: "",
    phone: "",
    agreedToTerms: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // Once the company exists its name can't be changed from this form
  // (re-submitting would otherwise silently keep the old name).
  const companyLocked = !!progress.current.companyId;

  const userName = useMemo(
    () => buildUserName(formData.firstName, formData.lastName),
    [formData.firstName, formData.lastName]
  );

  // Mark a step done and persist immediately
  const commit = (patch) => {
    progress.current = { ...progress.current, ...patch };
    saveProgress(progress.current);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "checkbox") {
      setFormData((p) => ({ ...p, [name]: checked }));
    } else if (type === "file") {
      const file = files[0] || null;

      try {
        logoValidationSchema.validateSyncAt("logo", { logo: file });
        setFormData((p) => ({ ...p, [name]: file }));
      } catch (err) {
        e.target.value = "";

        Swal.fire({
          icon: "error",
          title: "Invalid Logo",
          text: err.message,
        });
      }
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.agreedToTerms) {
      Swal.fire(
        "Terms Required",
        "You must agree to the Terms & Conditions",
        "warning"
      );
      return;
    }

    if (!formData.idno.trim()) {
      Swal.fire(
        "ID Number Required",
        "Please enter your ID Number before continuing.",
        "warning"
      );
      return;
    }

    setIsSubmitting(true);

    let logoFailed = false;

    try {
      // 1. Company — skipped if it already exists
      if (!progress.current.companyId) {
        setStatusText("Creating company...");

        const companyRes = await axios.post(
          `${companyManagementUrl.uri}/CreateCompany`,
          {
            companyName: formData.companyName.trim(),
            postalAddress: DEFAULT_ADDRESS,
            logoPath: null,
            trialDays: TRIAL_DAYS,
            isDiy: 1,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const companyData = parseResults(companyRes);

        if (!companyData?.Id) {
          throw new Error("Company creation failed.");
        }

        commit({
          companyId: companyData.Id,
          companyName: formData.companyName.trim(),
        });
      }

      // 1b. Logo — only if not already uploaded. Non-fatal; retried
      // on the next attempt as long as a file is selected.
      if (!progress.current.logoDone && formData.logo) {
        setStatusText("Uploading logo...");

        try {
          await uploadDocument(
            formData.logo,
            { companyId: progress.current.companyId },
            companyManagementUrl.upload_diylogo_uri
          );
          commit({ logoDone: true });
        } catch (logoErr) {
          console.error("Logo upload failed:", logoErr);
          logoFailed = true;
        }
      }

      // 2. Default region — skipped if it already exists
      if (!progress.current.regionId) {
        setStatusText("Setting up your workspace...");

        const regionRes = await axios.post(
          `${companyManagementUrl.uri}/CreateRegion`,
          {
            name: DEFAULT_REGION_NAME,
            companyId: progress.current.companyId,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const regionData = parseResults(regionRes);

        if (!regionData?.Id) {
          throw new Error("Failed to create default region.");
        }

        commit({ regionId: regionData.Id });
      }

      // 3. Default branch — skipped if it already exists
      if (!progress.current.branchDone) {
        setStatusText("Setting up your workspace...");

        const branchRes = await axios.post(
          `${companyManagementUrl.uri}/CreateBranch`,
          {
            name: DEFAULT_BRANCH_NAME,
            companyId: progress.current.companyId,
            regionId: progress.current.regionId,
            branchLocation: DEFAULT_BRANCH_LOCATION,
            phoneContact: DEFAULT_BRANCH_PHONE,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const branchData = parseResults(branchRes);

        if (!branchData?.Id) {
          throw new Error("Failed to create default branch.");
        }

        commit({ branchDone: true });
      }

      // 4. User — skipped if it already exists
      if (!progress.current.userDone) {
        setStatusText("Creating your account...");

        const now = new Date().toISOString();

        const userPayload = {
          id: 0,
          isDIYUser: 1,

          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          idno: formData.idno.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          userName,

          roleId: 0,
          isActive: 0,

          lastLogin: now,
          loginAttempts: 0,
          firstTimeLogin: 0,
          changePassword: 0,

          hashedPassword: "string",

          createdBy: 0,
          createdDate: now,

          updatedBy: 0,
          updatedDate: now,

          approvedBy: 0,
          approvedDate: now,

          deactivateBy: 0,
          deactivateDate: now,

          registrationStatus: 0,

          roleGroupId: 0,
          roleGroup: "string",

          isSupervisor: 0,
          isOnLeave: true,
          genderId: 0,

          companyId: progress.current.companyId,
          regionId: 0,
          branchId: 0,
          departmentId: 0,
          countryId: 0,

          unlockOT: "string",
          otpExpiry: now,

          isEncrypted: true,
          appVersion: "string",
        };

        const userRes = await axios.post(
          `${userManagementUrl.uri}/SignUp`,
          userPayload,
          { headers: { "Content-Type": "application/json" } }
        );

        let responseData = userRes.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch {
            // Keep raw response
          }
        }

        if (!(responseData?.message || responseData?.Id)) {
          throw new Error("Unexpected response format from server");
        }

        commit({ userDone: true });
      }

      // Everything succeeded — nothing left to resume
      clearProgress();
      setShowSuccess(true);

      if (logoFailed) {
        Swal.fire({
          icon: "info",
          title: "Logo not uploaded",
          text: "Your trial is ready, but the logo could not be uploaded. You can add it later in settings.",
        });
      }
    } catch (error) {
      console.error("Trial setup error:", error);

      const data = error.response?.data;

      const message =
        (typeof data === "object" && (data?.message || data?.error)) ||
        (typeof data === "string" && data) ||
        error.message ||
        "Setup failed";

      Swal.fire({
        icon: "error",
        title: "Setup Failed",
        text: `${message}. Please try again — only the steps that did not finish will be re-run.`,
      });
    } finally {
      setIsSubmitting(false);
      setStatusText("");
    }
  };

  const Header = ({ title }) => (
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-xl font-bold text-[#01da97]">{title}</h2>

      <button
        onClick={onClose}
        className="text-gray-300 hover:text-red-500 text-2xl font-bold"
        aria-label="Close"
      >
        &times;
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
      {showSuccess ? (
        <div className="max-w-md w-full mx-4 p-6 rounded-xl shadow-xl bg-[#0C111C] text-white">
          <Header title="You're all set!" />

          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto text-[#01da97]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>

            <p className="mt-4 text-[#01da97] font-semibold">
              Confirmation email sent to{" "}
              <span className="font-bold">{formData.email}</span>
            </p>

            <p className="text-sm mt-3">
              Confirm your email to activate your {TRIAL_DAYS}-day trial for{" "}
              <span className="font-semibold">{formData.companyName}</span>.
            </p>

            <p className="text-sm mt-3 text-gray-400">
              Your username is{" "}
              <span className="text-white font-semibold">{userName}</span>
            </p>

            <button
              className="mt-6 w-full bg-[#01da97] text-black px-6 py-2 rounded hover:bg-[#00c77a] font-semibold"
              onClick={() => (window.location.href = "/login")}
            >
              Proceed to Login
            </button>

            <p className="mt-4 text-xs text-gray-400">
              Can't find the email? Check your spam folder.
            </p>
          </div>
        </div>
      ) : (
        <div className="max-w-lg w-full mx-4 p-6 rounded-xl shadow-xl bg-[#0C111C] text-white">
          <Header title={`${TRIAL_DAYS}-Day Free Trial`} />

          <form
            onSubmit={handleSubmit}
            className="space-y-4 max-h-[80vh] overflow-y-auto"
          >
            {/* Company Name */}
            <div>
              <input
                type="text"
                name="companyName"
                placeholder="Company Name"
                value={formData.companyName}
                onChange={handleChange}
                required
                disabled={isSubmitting || companyLocked}
                className={`${inputClass} ${
                  companyLocked ? "opacity-60 cursor-not-allowed" : ""
                }`}
              />
              {companyLocked && (
                <p className="text-xs text-gray-400 mt-1">
                  Company already created — continuing where you left off.
                </p>
              )}
            </div>

            {/* Company Logo */}
            <div>
              <label
                htmlFor="logo"
                className="block text-sm text-gray-300 mb-1"
              >
                Company Logo{" "}
                <span className="text-gray-500">
                  (optional, PNG/JPEG, max 2MB)
                </span>
              </label>

              <input
                id="logo"
                type="file"
                name="logo"
                accept="image/png, image/jpeg"
                onChange={handleChange}
                disabled={isSubmitting || progress.current.logoDone}
                className={inputClass}
              />
            </div>

            {/* First Name / Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className={inputClass}
              />

              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className={inputClass}
              />
            </div>

            {/* ID Number */}
            <input
              type="text"
              name="idno"
              placeholder="ID Number"
              value={formData.idno}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className={inputClass}
            />

            {/* Email */}
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className={inputClass}
            />

            {/* Phone */}
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className={inputClass}
            />

            {/* Username Preview */}
            {userName && (
              <p className="text-xs text-gray-400">
                Your username will be:{" "}
                <span className="text-[#01da97] font-semibold">
                  {userName}
                </span>
              </p>
            )}

            {/* Terms */}
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="agreedToTerms"
                checked={formData.agreedToTerms}
                onChange={handleChange}
                disabled={isSubmitting}
                className="mr-2"
              />

              <span className="text-sm">
                I agree to the{" "}
                <a href="#" className="text-[#01da97] underline">
                  Terms &amp; Conditions
                </a>
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full px-6 py-2 rounded bg-[#01da97] text-black font-semibold ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#00c77a]"
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></span>
                  {statusText || "Processing..."}
                </span>
              ) : companyLocked ? (
                "Retry Setup"
              ) : (
                "Start Free Trial"
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default TrialFormWizard;